import { Button } from '../../../../../../widgets/buttons';
import { ColorPicker } from '../../../../../../widgets/colorPicker';
import { FormControl } from '../../../../../../widgets/formControl';
import { Input } from '../../../../../../widgets/input';
import { AdvancedSelect, compactSelectStyles } from '../../../../../../widgets/select';
import React, { useMemo } from 'react';
import { T } from 'src/shared/helpers';
import { ComposerMaterialPropertyType } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { PropertiesPanelNumberField, PropertiesPanelSwitch } from '../../../configurator-components/scene-properties-panel';

export interface MaterialPropertyInfo {
    label: string;
    value: string;
    type: ComposerMaterialPropertyType;
}

export interface MaterialPropertyGroup {
    label: string;
    value: string;
    subProperties: MaterialPropertyInfo[];
}

/**
 * Hook per gestire l'analisi delle proprietà dei materiali BABYLON.js
 * e la lettura dei valori reali del materiale
 */
export const useMaterialPropertyDetector = (material: BABYLON.Material | null) => {
    /**
     * Converte un colore BABYLON.Color3 in hex string
     */
    const color3ToHex = (color: BABYLON.Color3): string => {
        if (!color) return '#FFFFFF';

        const r = Math.round(color.r * 255);
        const g = Math.round(color.g * 255);
        const b = Math.round(color.b * 255);

        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    /**
     * Debug: elenca tutte le proprietà disponibili di un oggetto
     */
    const logObjectProperties = (obj: any, name: string, maxDepth: number = 2): void => {
        if (!obj || maxDepth <= 0) return;

        try {
            console.group(`🔍 Properties of ${name}:`);
            Object.keys(obj).forEach((key) => {
                const value = obj[key];
                const type = typeof value;

                if (type === 'object' && value !== null && maxDepth > 1) {
                    console.log(`📁 ${key}:`, value);
                    if (['clearCoat', 'anisotropy', 'sheen', 'subSurface'].includes(key)) {
                        logObjectProperties(value, `${name}.${key}`, maxDepth - 1);
                    }
                } else {
                    console.log(`${type === 'function' ? '🔧' : '📄'} ${key}: ${type === 'function' ? '[Function]' : value}`);
                }
            });
            console.groupEnd();
        } catch (error) {
            console.warn(`Error logging properties of ${name}:`, error);
        }
    };

    /**
     * Ottiene il valore reale di una proprietà dal materiale usando dot notation
     */
    const getPropertyValue = (material: any, propertyPath: string): string => {
        if (!material || !propertyPath) {
            return '';
        }

        try {
            const parts = propertyPath.split('.');
            let current = material;

            for (const part of parts) {
                if (current && typeof current === 'object' && part in current) {
                    current = current[part];
                } else {
                    return '';
                }
            }

            // Converti il valore basato sul tipo
            if (current === null || current === undefined) {
                return '';
            }

            // Se è un colore BABYLON.Color3
            if (current && typeof current === 'object' && 'r' in current && 'g' in current && 'b' in current) {
                const hexValue = color3ToHex(current as BABYLON.Color3);
                return hexValue;
            }

            // Se è un booleano
            if (typeof current === 'boolean') {
                return current.toString();
            }

            // Se è un numero
            if (typeof current === 'number') {
                return current.toString();
            }

            // Se è una texture
            if (current && typeof current === 'object' && 'name' in current) {
                return (current as any).name || 'texture';
            }

            // Default: converti a stringa
            const stringValue = current.toString();
            return stringValue;
        } catch (error) {
            console.warn(`❌ Error reading property ${propertyPath}:`, error);
            return '';
        }
    };

    /**
     * Ottiene tutti i gruppi di proprietà disponibili per un materiale
     */
    const getAvailablePropertyGroups = useMemo((): MaterialPropertyGroup[] => {
        if (!material) return [];

        const groups: MaterialPropertyGroup[] = [];
        const materialObj = material as any;

        // Determina il tipo di materiale
        const className = material.getClassName ? material.getClassName() : 'Unknown';
        const isPBR =
            className.includes('PBR') ||
            materialObj.metallicTexture !== undefined ||
            materialObj.roughness !== undefined ||
            materialObj.baseColor !== undefined;

        // Material Properties
        groups.push(createMaterialPropertiesGroup(materialObj));

        // Intensity
        groups.push(createIntensityGroup(materialObj));

        // Camera
        groups.push(createCameraGroup(materialObj));

        // Albedo/Base
        groups.push(createAlbedoGroup(materialObj, isPBR));

        // Ambient
        groups.push(createAmbientGroup(materialObj));

        // Opacity
        groups.push(createOpacityGroup(materialObj));

        // Reflection
        groups.push(createReflectionGroup(materialObj));

        // Emissive
        groups.push(createEmissiveGroup(materialObj));

        // Reflectivity
        groups.push(createReflectivityGroup(materialObj));

        // Metallic
        if (isPBR) {
            groups.push(createMetallicGroup(materialObj));
        }

        // Micro Surface
        groups.push(createMicroSurfaceGroup(materialObj));

        // Normal
        groups.push(createNormalGroup(materialObj));

        // Refraction
        groups.push(createRefractionGroup(materialObj));

        // Lightmap
        groups.push(createLightmapGroup(materialObj));

        // Flags
        groups.push(createFlagsGroup(materialObj));

        // Clear Coat
        groups.push(createClearCoatGroup(materialObj));

        // Anisotropic
        groups.push(createAnisotropicGroup(materialObj));

        // Sheen
        groups.push(createSheenGroup(materialObj));

        // SubSurface
        groups.push(createSubSurfaceGroup(materialObj));

        return groups;
    }, [material]);

    /**
     * Crea il gruppo Material Properties (proprietà di base del materiale)
     */
    function createMaterialPropertiesGroup(material: any): MaterialPropertyGroup {
        const subProperties: MaterialPropertyInfo[] = [];

        // Proprietà di base del materiale
        subProperties.push({ label: 'Opacity', value: 'alpha', type: ComposerMaterialPropertyType.NUMBER });
        subProperties.push({ label: 'Transparency Mode', value: 'transparencyMode', type: ComposerMaterialPropertyType.TEXTURE }); // Usiamo TEXTURE per gestire il select
        subProperties.push({ label: 'Double sided', value: 'backFaceCulling', type: ComposerMaterialPropertyType.BOOLEAN }); // Nota: il valore è invertito!
        subProperties.push({ label: 'Need Depth Pre-Pass', value: 'needDepthPrePass', type: ComposerMaterialPropertyType.BOOLEAN });
        subProperties.push({ label: 'Z Offset', value: 'zOffset', type: ComposerMaterialPropertyType.NUMBER });

        return {
            label: 'Material Properties',
            value: 'materialProperties',
            subProperties
        };
    }

    /**
     * Crea il gruppo Intensity
     */
    function createIntensityGroup(material: any): MaterialPropertyGroup {
        const subProperties: MaterialPropertyInfo[] = [];

        subProperties.push({ label: 'Direct', value: 'directIntensity', type: ComposerMaterialPropertyType.NUMBER });
        subProperties.push({ label: 'Emissive', value: 'emissiveIntensity', type: ComposerMaterialPropertyType.NUMBER });
        subProperties.push({ label: 'Environment', value: 'environmentIntensity', type: ComposerMaterialPropertyType.NUMBER });
        subProperties.push({ label: 'Specular', value: 'specularIntensity', type: ComposerMaterialPropertyType.NUMBER });

        return {
            label: 'Intensity',
            value: 'intensity',
            subProperties
        };
    }

    /**
     * Crea il gruppo Camera
     */
    function createCameraGroup(material: any): MaterialPropertyGroup {
        const subProperties: MaterialPropertyInfo[] = [];

        subProperties.push({ label: 'Exposure', value: 'exposure', type: ComposerMaterialPropertyType.NUMBER });
        subProperties.push({ label: 'Contrast', value: 'contrast', type: ComposerMaterialPropertyType.NUMBER });
        subProperties.push({ label: 'Texture', value: 'cameraTexture', type: ComposerMaterialPropertyType.TEXTURE });

        return {
            label: 'Camera',
            value: 'camera',
            subProperties
        };
    }

    /**
     * Crea il gruppo Albedo
     */
    function createAlbedoGroup(material: any, isPBR: boolean): MaterialPropertyGroup {
        const subProperties: MaterialPropertyInfo[] = [];

        if (isPBR) {
            // Per materiali PBR - controlla entrambe le proprietà
            if (material.albedoColor !== undefined || material.baseColor !== undefined) {
                subProperties.push({ label: 'Color', value: 'albedoColor', type: ComposerMaterialPropertyType.COLOR });
            }
            if (material.albedoTexture !== undefined || material.baseTexture !== undefined) {
                subProperties.push({ label: 'Texture', value: 'albedoTexture', type: ComposerMaterialPropertyType.TEXTURE });
            }
        } else {
            // Per materiali standard
            if (material.diffuseColor !== undefined) {
                subProperties.push({ label: 'Color', value: 'diffuseColor', type: ComposerMaterialPropertyType.COLOR });
            }
            if (material.diffuseTexture !== undefined) {
                subProperties.push({ label: 'Texture', value: 'diffuseTexture', type: ComposerMaterialPropertyType.TEXTURE });
            }
        }

        return {
            label: 'Albedo',
            value: 'albedo',
            subProperties
        };
    }

    /**
     * Crea il gruppo Ambient
     */
    function createAmbientGroup(material: any): MaterialPropertyGroup {
        const subProperties: MaterialPropertyInfo[] = [];

        if (material.ambientColor !== undefined) {
            subProperties.push({ label: 'Color', value: 'ambientColor', type: ComposerMaterialPropertyType.COLOR });
        }
        if (material.ambientTexture !== undefined) {
            subProperties.push({ label: 'Texture', value: 'ambientTexture', type: ComposerMaterialPropertyType.TEXTURE });
        }

        return {
            label: 'Ambient',
            value: 'ambient',
            subProperties
        };
    }

    /**
     * Crea il gruppo Opacity
     */
    function createOpacityGroup(material: any): MaterialPropertyGroup {
        const subProperties: MaterialPropertyInfo[] = [];

        subProperties.push({ label: 'Texture', value: 'opacityTexture', type: ComposerMaterialPropertyType.TEXTURE });

        return {
            label: 'Opacity',
            value: 'opacity',
            subProperties
        };
    }

    /**
     * Crea il gruppo Reflection
     */
    function createReflectionGroup(material: any): MaterialPropertyGroup {
        const subProperties: MaterialPropertyInfo[] = [];

        if (material.reflectionColor !== undefined) {
            subProperties.push({ label: 'Color', value: 'reflectionColor', type: ComposerMaterialPropertyType.COLOR });
        }
        if (material.reflectionTexture !== undefined) {
            subProperties.push({ label: 'Texture', value: 'reflectionTexture', type: ComposerMaterialPropertyType.TEXTURE });
        }

        return {
            label: 'Reflection',
            value: 'reflection',
            subProperties
        };
    }

    /**
     * Crea il gruppo Emissive
     */
    function createEmissiveGroup(material: any): MaterialPropertyGroup {
        const subProperties: MaterialPropertyInfo[] = [];

        if (material.emissiveColor !== undefined) {
            subProperties.push({ label: 'Color', value: 'emissiveColor', type: ComposerMaterialPropertyType.COLOR });
        }
        if (material.emissiveTexture !== undefined) {
            subProperties.push({ label: 'Texture', value: 'emissiveTexture', type: ComposerMaterialPropertyType.TEXTURE });
        }

        return {
            label: 'Emissive',
            value: 'emissive',
            subProperties
        };
    }

    /**
     * Crea il gruppo Reflectivity
     */
    function createReflectivityGroup(material: any): MaterialPropertyGroup {
        const subProperties: MaterialPropertyInfo[] = [];

        if (material.reflectivityColor !== undefined) {
            subProperties.push({ label: 'Color', value: 'reflectivityColor', type: ComposerMaterialPropertyType.COLOR });
        }
        if (material.reflectivityTexture !== undefined) {
            subProperties.push({ label: 'Texture', value: 'reflectivityTexture', type: ComposerMaterialPropertyType.TEXTURE });
        }

        return {
            label: 'Reflectivity',
            value: 'reflectivity',
            subProperties
        };
    }

    /**
     * Crea il gruppo Metallic (solo PBR)
     */
    function createMetallicGroup(material: any): MaterialPropertyGroup {
        const subProperties: MaterialPropertyInfo[] = [];

        // Aggiungiamo SEMPRE tutte le proprietà metallic disponibili
        subProperties.push({ label: 'Metallic', value: 'metallic', type: ComposerMaterialPropertyType.NUMBER });
        subProperties.push({ label: 'Roughness', value: 'roughness', type: ComposerMaterialPropertyType.NUMBER });
        subProperties.push({ label: 'Reflectance color', value: 'reflectanceColor', type: ComposerMaterialPropertyType.COLOR });
        subProperties.push({ label: 'Texture', value: 'metallicTexture', type: ComposerMaterialPropertyType.TEXTURE });

        return {
            label: 'Metallic',
            value: 'metallic',
            subProperties
        };
    }

    /**
     * Crea il gruppo Micro Surface
     */
    function createMicroSurfaceGroup(material: any): MaterialPropertyGroup {
        const subProperties: MaterialPropertyInfo[] = [];

        if (material.microSurface !== undefined) {
            subProperties.push({ label: 'Micro Surface', value: 'microSurface', type: ComposerMaterialPropertyType.NUMBER });
        }
        if (material.microSurfaceTexture !== undefined) {
            subProperties.push({ label: 'Texture', value: 'microSurfaceTexture', type: ComposerMaterialPropertyType.TEXTURE });
        }

        return {
            label: 'Micro Surface',
            value: 'microSurface',
            subProperties
        };
    }

    /**
     * Crea il gruppo Normal
     */
    function createNormalGroup(material: any): MaterialPropertyGroup {
        const subProperties: MaterialPropertyInfo[] = [];

        if (material.normalTexture !== undefined) {
            subProperties.push({ label: 'Texture', value: 'normalTexture', type: ComposerMaterialPropertyType.TEXTURE });
        }
        if (material.bumpTexture !== undefined) {
            subProperties.push({ label: 'Bump Texture', value: 'bumpTexture', type: ComposerMaterialPropertyType.TEXTURE });
        }

        return {
            label: 'Normal',
            value: 'normal',
            subProperties
        };
    }

    /**
     * Crea il gruppo Refraction
     */
    function createRefractionGroup(material: any): MaterialPropertyGroup {
        const subProperties: MaterialPropertyInfo[] = [];

        subProperties.push({ label: 'Texture', value: 'refractionTexture', type: ComposerMaterialPropertyType.TEXTURE });

        return {
            label: 'Refraction',
            value: 'refraction',
            subProperties
        };
    }

    /**
     * Crea il gruppo Lightmap
     */
    function createLightmapGroup(material: any): MaterialPropertyGroup {
        const subProperties: MaterialPropertyInfo[] = [];

        if (material.lightmapTexture !== undefined) {
            subProperties.push({ label: 'Texture', value: 'lightmapTexture', type: ComposerMaterialPropertyType.TEXTURE });
        }

        return {
            label: 'Lightmap',
            value: 'lightmap',
            subProperties
        };
    }

    /**
     * Crea il gruppo Flags
     */
    function createFlagsGroup(material: any): MaterialPropertyGroup {
        const subProperties: MaterialPropertyInfo[] = [];

        subProperties.push({ label: 'Lightmap as shadowmap', value: 'useLightmapAsShadowmap', type: ComposerMaterialPropertyType.BOOLEAN });
        subProperties.push({ label: 'Specular over alpha', value: 'useSpecularOverAlpha', type: ComposerMaterialPropertyType.BOOLEAN });
        subProperties.push({ label: 'Physical light falloff', value: 'usePhysicalLightFalloff', type: ComposerMaterialPropertyType.BOOLEAN });
        subProperties.push({ label: 'Radiance over alpha', value: 'useRadianceOverAlpha', type: ComposerMaterialPropertyType.BOOLEAN });
        subProperties.push({ label: 'Parallax', value: 'useParallax', type: ComposerMaterialPropertyType.BOOLEAN });
        subProperties.push({ label: 'Parallax occlusion', value: 'useParallaxOcclusion', type: ComposerMaterialPropertyType.BOOLEAN });
        subProperties.push({ label: 'Disable lighting', value: 'disableLighting', type: ComposerMaterialPropertyType.BOOLEAN });
        subProperties.push({ label: 'Two sided lighting', value: 'twoSidedLighting', type: ComposerMaterialPropertyType.BOOLEAN });

        return {
            label: 'Flags',
            value: 'flags',
            subProperties
        };
    }

    /**
     * Crea il gruppo Clear Coat completo
     */
    function createClearCoatGroup(material: any): MaterialPropertyGroup {
        const subProperties: MaterialPropertyInfo[] = [];

        // Proprietà dirette Clear Coat
        subProperties.push({ label: 'Intensity', value: 'clearCoat.intensity', type: ComposerMaterialPropertyType.NUMBER });
        subProperties.push({ label: 'Index of refraction', value: 'clearCoat.indexOfRefraction', type: ComposerMaterialPropertyType.NUMBER });
        subProperties.push({ label: 'Texture', value: 'clearCoat.texture', type: ComposerMaterialPropertyType.TEXTURE });
        subProperties.push({ label: 'Roughness', value: 'clearCoat.roughness', type: ComposerMaterialPropertyType.NUMBER });
        subProperties.push({ label: 'Remap F0', value: 'clearCoat.remapF0', type: ComposerMaterialPropertyType.BOOLEAN });

        // Sotto-sezioni
        subProperties.push({ label: 'Bump', value: 'clearCoat_bump_section', type: ComposerMaterialPropertyType.TEXTURE });
        subProperties.push({ label: 'Tint', value: 'clearCoat_tint_section', type: ComposerMaterialPropertyType.TEXTURE });
        subProperties.push({ label: 'Roughness (sub-property)', value: 'clearCoat_roughness_section', type: ComposerMaterialPropertyType.TEXTURE });

        return {
            label: 'Clear Coat',
            value: 'clearcoat',
            subProperties
        };
    }

    /**
     * Crea il gruppo Anisotropic
     */
    function createAnisotropicGroup(material: any): MaterialPropertyGroup {
        // Aggiungiamo SEMPRE tutte le proprietà Anisotropic con i nomi corretti BABYLON.js
        const subProperties: MaterialPropertyInfo[] = [
            { label: 'Intensity', value: 'anisotropy.intensity', type: ComposerMaterialPropertyType.NUMBER },
            { label: 'Direction X', value: 'anisotropy.direction.x', type: ComposerMaterialPropertyType.NUMBER },
            { label: 'Direction Y', value: 'anisotropy.direction.y', type: ComposerMaterialPropertyType.NUMBER },
            { label: 'Texture', value: 'anisotropy.texture', type: ComposerMaterialPropertyType.TEXTURE }
        ];

        return {
            label: 'Anisotropic',
            value: 'anisotropic',
            subProperties
        };
    }

    /**
     * Crea il gruppo Sheen
     */
    function createSheenGroup(material: any): MaterialPropertyGroup {
        // Aggiungiamo SEMPRE tutte le proprietà Sheen (rimuoviamo Enabled)
        const subProperties: MaterialPropertyInfo[] = [
            { label: 'Intensity', value: 'sheen.intensity', type: ComposerMaterialPropertyType.NUMBER },
            { label: 'Albedo Scaling', value: 'sheen.albedoScaling', type: ComposerMaterialPropertyType.BOOLEAN },
            { label: 'Link Sheen With Albedo', value: 'sheen.linkSheenWithAlbedo', type: ComposerMaterialPropertyType.BOOLEAN },
            { label: 'Color', value: 'sheen.color', type: ComposerMaterialPropertyType.COLOR },
            { label: 'Texture', value: 'sheen.texture', type: ComposerMaterialPropertyType.TEXTURE }
        ];

        return {
            label: 'Sheen',
            value: 'sheen',
            subProperties
        };
    }

    /**
     * Crea il gruppo SubSurface completo (include Thickness e Tint)
     */
    function createSubSurfaceGroup(material: any): MaterialPropertyGroup {
        const subProperties: MaterialPropertyInfo[] = [];

        // Proprietà dirette SubSurface
        if (material.subSurface?.useGltfStyleTextures !== undefined) {
            subProperties.push({ label: 'Use gltf style textures', value: 'subSurface.useGltfStyleTextures', type: ComposerMaterialPropertyType.BOOLEAN });
        }

        // Sotto-sezioni
        subProperties.push({ label: 'Thickness', value: 'subsurface_thickness_section', type: ComposerMaterialPropertyType.TEXTURE });
        subProperties.push({ label: 'Tint', value: 'subsurface_tint_section', type: ComposerMaterialPropertyType.COLOR });
        subProperties.push({ label: 'Refraction', value: 'subsurface_refraction_section', type: ComposerMaterialPropertyType.NUMBER });
        subProperties.push({ label: 'Translucency', value: 'subsurface_translucency_section', type: ComposerMaterialPropertyType.BOOLEAN });

        return {
            label: 'SubSurface',
            value: 'subsurface',
            subProperties
        };
    }

    /**
     * Ottiene il range corretto per una proprietà numerica specifica
     */
    const getPropertyRange = (propertyValue: string): { min: number; max: number; step: number } => {
        const ranges: Record<string, { min: number; max: number; step: number }> = {
            // Clear Coat
            'clearCoat.intensity': { min: 0, max: 1, step: 0.01 },
            'clearCoat.roughness': { min: 0, max: 1, step: 0.01 },
            'clearCoat.indexOfRefraction': { min: 0, max: 2, step: 0.01 },
            'clearCoat.tintColorAtDistance': { min: 0, max: 1, step: 0.1 },
            'clearCoat.tintThickness': { min: 0, max: 1, step: 0.01 },

            // SubSurface
            'subSurface.maximumThickness': { min: 0, max: 1, step: 0.01 },
            'subSurface.minimumThickness': { min: 0, max: 1, step: 0.01 },
            'subSurface.thicknessFactor': { min: 0, max: 1, step: 0.01 },
            'subSurface.refractionIntensity': { min: 0, max: 1, step: 0.01 },
            'subSurface.translucencyIntensity': { min: 0, max: 1, step: 0.01 },

            // Material Properties
            alpha: { min: 0, max: 1, step: 0.01 },
            zOffset: { min: 0, max: 100, step: 1 },

            // Intensities
            directIntensity: { min: 0, max: 1, step: 0.1 },
            emissiveIntensity: { min: 0, max: 1, step: 0.1 },
            environmentIntensity: { min: 0, max: 1, step: 0.1 },
            specularIntensity: { min: 0, max: 1, step: 0.1 },

            // Altri
            roughness: { min: 0, max: 1, step: 0.01 },
            metallicFactor: { min: 0, max: 1, step: 0.01 },
            microSurface: { min: 0, max: 1, step: 0.01 },
            'bumpTexture.level': { min: 0, max: 2, step: 0.01 },
            indexOfRefraction: { min: 0, max: 2, step: 0.01 }
        };

        return ranges[propertyValue] || { min: 0, max: 1, step: 0.01 };
    };

    /**
     * Rendering del controllo appropriato per una proprietà
     */
    const renderPropertyControl = (property: MaterialPropertyInfo, value: string, onChange: (value: string) => void): React.ReactElement => {
        // SEMPRE leggi il valore reale dal materiale per il display "Current value"
        const realValue = getPropertyValue(material, property.value);

        // Il controllo usa il 'value' passato dal dialog, dando priorità al valore del dialog
        // Se value è undefined/null, usa realValue, ma se è stringa vuota la mantiene
        const controlValue = value !== undefined && value !== null ? value : realValue;

        // CASO SPECIALE: ClearCoat Roughness Section - renderizza checkbox + texture insieme
        if (property.value === 'clearCoat.useRoughnessFromMainTexture' || property.value === 'clearCoat_textureRoughnessTexture') {
            console.log('🔧 materialPropertyDetector: Rendering clearCoat roughness special case');
            console.log('  - property.value:', property.value);
            console.log('  - value passed:', value);

            // Leggi entrambi i valori dal materiale
            const mat = material as any;
            const realCheckboxValue = mat?.clearCoat?.useRoughnessFromMainTexture || false;
            const realTextureValue = mat?.clearCoat?.texture?.name || 'None';

            // Determina quale proprietà è selezionata
            const isCheckboxProperty = property.value === 'clearCoat.useRoughnessFromMainTexture';
            const isTextureProperty = property.value === 'clearCoat_textureRoughnessTexture';

            // Prova a parsare il value come JSON per ottenere entrambi i valori
            let checkboxValueFromDialog = realCheckboxValue;
            let textureValueFromDialog = '';

            console.log('  📦 Trying to parse value:', value);
            console.log('  📦 value type:', typeof value);
            console.log('  📦 value starts with {?:', value && value.startsWith('{'));

            try {
                if (value && value.startsWith('{')) {
                    console.log('  ✅ Parsing JSON state object');
                    const parsed = JSON.parse(value);
                    console.log('  ✅ Parsed:', parsed);
                    checkboxValueFromDialog = parsed.checkboxValue;
                    textureValueFromDialog = parsed.textureValue || '';
                    console.log('  ✅ checkboxValueFromDialog:', checkboxValueFromDialog);
                    console.log('  ✅ textureValueFromDialog:', textureValueFromDialog);
                } else {
                    console.log('  ⚠️ Using fallback method');
                    // Fallback: usa il vecchio metodo
                    if (isCheckboxProperty) {
                        checkboxValueFromDialog = controlValue === 'true';
                    }
                    if (isTextureProperty) {
                        textureValueFromDialog = controlValue;
                    }
                }
            } catch (e) {
                console.warn('❌ Could not parse roughness state:', e);
                // Fallback
                checkboxValueFromDialog = isCheckboxProperty ? controlValue === 'true' : realCheckboxValue;
                textureValueFromDialog = isTextureProperty ? controlValue : '';
            }

            return (
                <>
                    {/* Interruttore - sempre visibile */}
                    <FormControl label={T._('Use roughness from main texture', 'Admin')}>
                        <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                            {T._('Current value', 'Admin')}: <strong>{realCheckboxValue ? 'Enabled' : 'Disabled'}</strong>
                        </div>
                        <PropertiesPanelSwitch
                            ariaLabel={T._('Use roughness from main texture', 'Admin')}
                            checked={checkboxValueFromDialog}
                            onChange={(checked: boolean) => {
                                console.log('  🔄 Checkbox changed to:', checked);
                                // Se abiliti la checkbox, passa un valore speciale per dire al dialog di svuotare la texture
                                if (checked) {
                                    onChange(`checkbox:${checked.toString()}|clearTexture`);
                                } else {
                                    onChange(`checkbox:${checked.toString()}`);
                                }
                            }}
                        />
                    </FormControl>

                    {/* Input Texture - sempre visibile ma disabilitata se checkbox è true */}
                    <FormControl label={T._('Roughness Texture', 'Admin')}>
                        <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                            {T._('Current value', 'Admin')}: <strong>{realTextureValue}</strong>
                        </div>
                        <Input
                            type='url'
                            placeholder={T._('Enter texture URL (e.g., https://example.com/texture.jpg)', 'Admin')}
                            disabled={checkboxValueFromDialog}
                            value={textureValueFromDialog}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                console.log('  🔄 Texture input changed to:', e.target.value);
                                // Passa sempre il valore al dialog, che gestirà il cambio di proprietà se necessario
                                // Il valore viene prefissato con 'texture:' per distinguerlo dal checkbox value
                                onChange(`texture:${e.target.value}`);
                            }}
                            style={{
                                opacity: checkboxValueFromDialog ? 0.5 : 1,
                                cursor: checkboxValueFromDialog ? 'not-allowed' : 'text'
                            }}
                        />

                        {/* Bottone Clear - solo se c'è un valore e la checkbox è disabilitata */}
                        {!checkboxValueFromDialog && textureValueFromDialog && textureValueFromDialog.trim() !== '' && (
                            <div style={{ marginTop: '8px' }}>
                                <Button
                                    $size='small'
                                    onClick={() => {
                                        console.log('🧹 Clear roughness texture clicked');
                                        onChange('texture:');
                                    }}
                                >
                                    {T._('Clear', 'Admin')}
                                </Button>
                            </div>
                        )}
                    </FormControl>
                </>
            );
        }

        // Casi speciali
        if (property.value === 'transparencyMode') {
            // Transparency Mode - gestisci come select
            const transparencyOptions = [
                { value: '-1', label: 'Auto' },
                { value: '0', label: 'Opaque' },
                { value: '1', label: 'Alpha Test' },
                { value: '2', label: 'Alpha Blend' },
                { value: '3', label: 'Alpha Blend + Test' }
            ];

            const realMode = realValue || '-1';
            const selectedOption = transparencyOptions.find((opt) => opt.value === realMode) || transparencyOptions[0];

            return (
                <FormControl label={T._(property.label, 'Admin')}>
                    <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                        {T._('Current value', 'Admin')}: <strong>{selectedOption.label}</strong>
                    </div>
                    <AdvancedSelect
                        aria-label={T._(property.label, 'Admin')}
                        options={transparencyOptions}
                        value={transparencyOptions.find((option) => option.value === String(controlValue || realMode)) ?? null}
                        onChange={(option: { value: string } | null) => onChange(option?.value ?? '')}
                        styles={compactSelectStyles as any}
                        menuPortalTarget={document.body}
                        menuPlacement='auto'
                    />
                </FormControl>
            );
        }

        if (property.value === 'backFaceCulling') {
            // Double sided - logica invertita (doubleSided = !backFaceCulling)
            const realBackFaceCulling = (typeof realValue === 'boolean' && realValue) || realValue === 'true';
            const realDoubleSided = !realBackFaceCulling;

            const controlDoubleSided = controlValue ? !(controlValue === 'true') : realDoubleSided;

            return (
                <FormControl label={T._(property.label, 'Admin')}>
                    <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                        {T._('Current value', 'Admin')}: <strong>{realDoubleSided ? 'Enabled' : 'Disabled'}</strong>
                    </div>
                    <PropertiesPanelSwitch
                        ariaLabel={T._(property.label, 'Admin')}
                        checked={controlDoubleSided}
                        onChange={(checked) => onChange((!checked).toString())}
                    />
                </FormControl>
            );
        }

        // Gestione tipi standard
        switch (property.type) {
            case ComposerMaterialPropertyType.COLOR:
                const realColorValue = realValue && realValue.startsWith('#') ? realValue : '#FFFFFF';
                const controlColorValue = controlValue && controlValue.startsWith('#') ? controlValue : realColorValue;

                return (
                    <FormControl label={T._(property.label, 'Admin')}>
                        <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                            {T._('Material original value', 'Admin')}: <strong>{realColorValue}</strong>
                        </div>
                        <div style={{ marginBottom: '8px', fontSize: '12px', color: '#333' }}>
                            {T._('Selected value', 'Admin')}: <strong>{controlColorValue}</strong>
                        </div>
                        <ColorPicker
                            key={`colorpicker-${property.value}-${controlColorValue}`}
                            color={controlColorValue}
                            onChange={(newColor) => {
                                onChange(newColor);
                            }}
                        />
                    </FormControl>
                );

            case ComposerMaterialPropertyType.NUMBER:
                const range = getPropertyRange(property.value);
                const realNumericValue = !isNaN(parseFloat(realValue)) ? parseFloat(realValue) : range.min;
                const controlNumericValue = controlValue && !isNaN(parseFloat(controlValue)) ? parseFloat(controlValue) : realNumericValue;

                return (
                    <FormControl label={T._(property.label, 'Admin')}>
                        <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                            {T._('Current value', 'Admin')}: <strong>{realNumericValue}</strong>
                        </div>
                        {/* LO SLIDER E IL NUMERO SONO UN CAMPO SOLO, come nella scena: si riempie fino al valore e si
                            trascina dentro l'intervallo. Il tetto vale per lo slider e non per ciò che si scrive — prima il
                            campo numerico accanto accettava anche un valore oltre il massimo (un'intensità sopra 1). */}
                        <PropertiesPanelNumberField
                            full
                            ariaLabel={T._(property.label, 'Admin')}
                            min={range.min}
                            step={range.step}
                            range={{ min: range.min, max: range.max }}
                            value={controlNumericValue}
                            onChange={(next) => onChange(String(next))}
                        />
                    </FormControl>
                );

            case ComposerMaterialPropertyType.BOOLEAN:
                const realBoolValue = (typeof realValue === 'boolean' && realValue) || realValue === 'true';
                const controlBoolValue = controlValue ? controlValue === 'true' : realBoolValue;

                // Log solo per sheen properties
                if (property.value.includes('sheen')) {
                    console.log('🔵 [SHEEN BOOLEAN] Property:', property.value, '| Value:', controlBoolValue);
                }

                return (
                    <FormControl label={T._(property.label, 'Admin')}>
                        <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                            {T._('Current value', 'Admin')}: <strong>{realBoolValue ? 'Enabled' : 'Disabled'}</strong>
                        </div>
                        <PropertiesPanelSwitch
                            ariaLabel={T._(property.label, 'Admin')}
                            checked={controlBoolValue}
                            onChange={(checked) => {
                                // Log solo per sheen properties
                                if (property.value.includes('sheen')) {
                                    console.log('🟢 [SHEEN CHANGED]', property.value, '→', checked);
                                }
                                onChange(checked.toString());
                            }}
                        />
                    </FormControl>
                );

            case ComposerMaterialPropertyType.TEXTURE:
                const realTextureName = realValue || 'None';

                return (
                    <FormControl label={T._(property.label, 'Admin')}>
                        <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                            {T._('Current value', 'Admin')}: <strong>{realTextureName}</strong>
                        </div>
                        {renderTexturePropertyControl(property, controlValue !== undefined ? controlValue : '', onChange)}
                    </FormControl>
                );

            default:
                return <div>Unsupported property type</div>;
        }
    };

    // Interfaccia per il valore texture quando è un oggetto
    interface TextureValue {
        property: string;
        subProperty: string;
        subSubProperty: string | null;
        value: string;
        propertyPath: string;
    }

    const renderTexturePropertyControl = (
        property: MaterialPropertyInfo,
        value: string | TextureValue,
        onChange: (value: string) => void
    ): React.ReactElement => {
        // Estrai il valore effettivo con type guard robusto e parsing JSON se necessario
        // questo perchè l'intero value arriva come esempio:
        // {"property":"albedo","subProperty":"albedoTexture","subSubProperty":null,"value":"errr","propertyPath":"albedoTexture"}
        let actualValue = '';

        if (typeof value === 'string') {
            // Se è una stringa, potrebbe essere JSON stringificato
            try {
                const parsed = JSON.parse(value);
                actualValue = parsed.value || '';
            } catch {
                // Non è JSON, usa la stringa così com'è
                actualValue = value;
            }
        } else if (value && typeof value === 'object') {
            // È già un oggetto, estrai il valore
            actualValue = (value as TextureValue)?.value || '';
        }

        console.log(`🎯 renderTexturePropertyControl: property='${property.value}', actualValue='${actualValue}', original value:`, value);

        return (
            <div style={{ marginTop: '8px' }}>
                <Input
                    type='url'
                    value={actualValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const newValue = e.target.value;
                        console.log(`🔄 Input onChange: '${newValue}'`);
                        onChange(newValue);
                    }}
                    placeholder={T._('Enter texture URL (e.g., https://example.com/texture.jpg)', 'Admin')}
                />

                {actualValue && actualValue.trim() !== '' && (
                    <div style={{ marginTop: '8px' }}>
                        <Button
                            $size='small'
                            onClick={() => {
                                console.log(`🧹 Clear button clicked for '${property.value}'`);
                                onChange('');
                            }}
                        >
                            {T._('Clear', 'Admin')}
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    /**
     * Ottieni le sub-sub-properties per una sub-property specifica
     */
    const getSubSubProperties = (propertyValue: string, subPropertyValue: string): MaterialPropertyInfo[] => {
        // Clear Coat sub-sections
        if (propertyValue === 'clearcoat') {
            switch (subPropertyValue) {
                case 'clearCoat_bump_section':
                    return [{ label: 'Texture', value: 'clearCoat_bumpTexture', type: ComposerMaterialPropertyType.TEXTURE }];
                case 'clearCoat_tint_section':
                    return [
                        { label: 'Color', value: 'clearCoat.tintColor', type: ComposerMaterialPropertyType.COLOR },
                        { label: 'Tint Color At Distance', value: 'clearCoat.tintColorAtDistance', type: ComposerMaterialPropertyType.NUMBER },
                        { label: 'Tint Thickness', value: 'clearCoat.tintThickness', type: ComposerMaterialPropertyType.NUMBER },
                        { label: 'Texture', value: 'clearCoat_tintTexture', type: ComposerMaterialPropertyType.TEXTURE }
                    ];
                case 'clearCoat_roughness_section':
                    // Solo se useRoughnessFromMainTexture è false, mostra la texture
                    const useRoughnessFromMain = getPropertyValue(material, 'clearCoat.useRoughnessFromMainTexture');
                    const properties = [
                        { label: 'Use roughness from main texture', value: 'clearCoat.useRoughnessFromMainTexture', type: ComposerMaterialPropertyType.BOOLEAN }
                    ];

                    // Aggiungi la texture solo se NON usa roughness dalla texture principale
                    if (!useRoughnessFromMain) {
                        properties.push({ label: 'Texture', value: 'clearCoat_textureRoughnessTexture', type: ComposerMaterialPropertyType.TEXTURE });
                    }

                    return properties;
            }
        }

        // SubSurface sub-sections
        if (propertyValue === 'subsurface') {
            switch (subPropertyValue) {
                case 'subsurface_thickness_section':
                    return [
                        { label: 'Maximum thickness', value: 'subSurface.maximumThickness', type: ComposerMaterialPropertyType.NUMBER },
                        { label: 'Minimum thickness', value: 'subSurface.minimumThickness', type: ComposerMaterialPropertyType.NUMBER },
                        {
                            label: 'Use mask from thickness texture',
                            value: 'subSurface.useMaskFromThicknessTexture',
                            type: ComposerMaterialPropertyType.BOOLEAN
                        },
                        { label: 'Use thickness as depth', value: 'subSurface.useThicknessAsDepth', type: ComposerMaterialPropertyType.BOOLEAN },
                        { label: 'Texture', value: 'subSurface_thicknessTexture', type: ComposerMaterialPropertyType.TEXTURE }
                    ];
                case 'subsurface_tint_section':
                    return [
                        { label: 'Tint color', value: 'subSurface.tintColor', type: ComposerMaterialPropertyType.COLOR },
                        { label: 'Tint color at distance', value: 'subSurface.tintColorAtDistance', type: ComposerMaterialPropertyType.NUMBER }
                    ];
                case 'subsurface_refraction_section':
                    return [
                        { label: 'Intensity', value: 'subSurface.refractionIntensity', type: ComposerMaterialPropertyType.NUMBER },
                        { label: 'Index of refraction', value: 'subSurface.indexOfRefraction', type: ComposerMaterialPropertyType.NUMBER },
                        { label: 'Invert refraction Y', value: 'subSurface.invertRefractionY', type: ComposerMaterialPropertyType.BOOLEAN },
                        {
                            label: 'Link refraction with transparency',
                            value: 'subSurface.linkRefractionWithTransparency',
                            type: ComposerMaterialPropertyType.BOOLEAN
                        },
                        { label: 'Use albedo to tint refraction', value: 'subSurface.useAlbedoToTintRefraction', type: ComposerMaterialPropertyType.BOOLEAN },
                        { label: 'Texture', value: 'subSurface_refractionIntensityTexture', type: ComposerMaterialPropertyType.TEXTURE }
                    ];
                case 'subsurface_translucency_section':
                    return [
                        {
                            label: 'Use albedo to tint translucency',
                            value: 'subSurface.useAlbedoToTintTranslucency',
                            type: ComposerMaterialPropertyType.BOOLEAN
                        },
                        { label: 'Diffusion distance', value: 'subSurface.diffusionDistance', type: ComposerMaterialPropertyType.COLOR },
                        { label: 'Translucency intensity', value: 'subSurface.translucencyIntensity', type: ComposerMaterialPropertyType.NUMBER },
                        { label: 'Texture', value: 'subSurface_translucencyTexture', type: ComposerMaterialPropertyType.TEXTURE }
                    ];
            }
        }

        // Se non è una sezione speciale, return empty array
        return [];
    };

    /**
     * Verifica se una sub-property ha delle sub-sub-properties
     */
    const hasSubSubProperties = (propertyValue: string, subPropertyValue: string): boolean => {
        return getSubSubProperties(propertyValue, subPropertyValue).length > 0;
    };

    // Return dell'hook
    return {
        getAvailablePropertyGroups,
        getPropertyValue,
        renderPropertyControl,
        renderTexturePropertyControl,
        getSubSubProperties,
        hasSubSubProperties
    };
};
